// M31 — Sentry init. Pulled into its own module so main.tsx stays
// lean and so we can unit-test the scrubbers if they grow.
//
// What we capture:
//   - Uncaught errors + unhandled promise rejections
//   - Errors thrown from React render (via the ErrorBoundary in App.tsx)
//   - 10% of perf traces (route navigations, supabase calls)
//
// What we explicitly DON'T capture:
//   - PII (emails, names): scrubbed in beforeSend below
//   - JWTs / access tokens in URLs: scrubbed in beforeSend
//   - Session replay (not enabled — adds bundle weight, can opt in later)
//
// Sentry's `sendDefaultPii` is left FALSE (the default), which already
// keeps IPs, cookies, and Authorization headers out of events. The
// scrubber below catches the rest.
//
// Releases match the git tag: __APP_VERSION__ is baked in by Vite at
// build time (see vite.config.ts), so each deploy lines up with a
// release in Sentry. The Sentry Vite plugin uploads source maps under
// the same release name, which means stack traces show real filenames
// and line numbers, not minified gibberish.

import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // No DSN means we're in local dev (or the env var wasn't set on the
  // build). Skip init entirely — no point emitting events to nowhere.
  if (!dsn) {
    if (import.meta.env.DEV) {
      // Quiet log so dev console doesn't get noisy
      console.info('[sentry] VITE_SENTRY_DSN not set, skipping init');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,

    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // 10% perf sample — enough to spot regressions without flooding
    // the free-tier quota.
    tracesSampleRate: 0.1,

    // Default false. Keeps IPs / cookies / Authorization headers out
    // of events. Leave this off unless we have a specific reason.
    sendDefaultPii: false,

    beforeSend(event) {
      // Strip JWT-style tokens that sometimes leak into breadcrumb
      // URLs (Supabase auth redirects, password-reset links).
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => {
          if (bc.data && typeof bc.data === 'object' && 'url' in bc.data && typeof bc.data.url === 'string') {
            bc.data.url = scrubUrl(bc.data.url);
          }
          return bc;
        });
      }
      return event;
    },
  });
}

// Best-effort URL scrubber — strips obvious token-looking query params.
// Not exhaustive; just catches the common cases (Supabase access_token,
// recovery tokens, magic links).
function scrubUrl(url: string): string {
  return url.replace(
    /(access_token|refresh_token|token|jwt|code|api_key|apikey)=[^&#]+/gi,
    '$1=<redacted>',
  );
}
